import { useOrganization } from '@library/react-toolkit'
import { either, task } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useShowError } from '../Notifications'
import { createTax, deleteTax, getTax, getTaxes, updateTax } from './api'
import { Tax, TaxesSearchRequest, NewTax, Nullable } from './types'

export const useTaxes = (req: Partial<Nullable<TaxesSearchRequest>>) => {
  const org = useOrganization()
  const showError = useShowError()

  return useQuery(
    [`${org?.name}-taxes`, req],
    pipe(
      getTaxes({ ...req, organizationName: org?.name ?? '' }),
      task.map(
        either.fold(
          e => {
            throw e
          },
          x => x
        )
      )
    ),
    {
      enabled: !!org?.name,
      keepPreviousData: true,
      staleTime: 60 * 1000,
      onError: () => {
        showError('getTaxesError')
      },
    }
  )
}

export const useTax = (id?: number) => {
  const org = useOrganization()
  const showError = useShowError()

  return useQuery(
    [`${org?.name}-tax`, id ?? 0],
    pipe(
      getTax(org?.name ?? '', id ?? 0),
      task.map(
        either.fold(
          e => {
            throw e
          },
          x => x
        )
      )
    ),
    {
      enabled: !!org?.name && !!id,
      staleTime: 60 * 1000,
      onError: () => {
        showError('getTaxesError')
      },
    }
  )
}

export const useAddTax = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (tax: NewTax) =>
      pipe(
        createTax({ organization: org?.name ?? '', ...tax }),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`${org?.name}-taxes`)
      },
      onError: () => {
        showError('addTaxError')
      },
    }
  )
}

export const useDeleteTax = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (id: number) =>
      pipe(
        deleteTax(org?.name ?? '', id),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(`${org?.name}-taxes`)
        queryClient.invalidateQueries([`${org?.name}-tax`, id])
      },
      onError: () => {
        showError('deleteTaxError')
      },
    }
  )
}

export const useUpdateTax = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (tax: Tax) =>
      pipe(
        updateTax({ organization: org?.name ?? '', ...tax }),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(`${org?.name}-taxes`)
        queryClient.invalidateQueries([`${org?.name}-tax`, id])
      },
      onError: () => {
        showError('editTaxError')
      },
    }
  )
}
